import {
  Amount,
  AMOUNT_ZERO,
  ChecksumAddress,
  DEFAULT_PROTOCOL_KEY,
  getChainKeyFromProtocol,
  LINKS,
  makeAddressSync,
  makeAmount,
  ProtocolKey,
  RUNTIME_PROTOCOL_INSTANCES,
} from '@silo/types';
import {
  MobileNav as BaseMobileNav,
  DiscordIcon,
  UnstyledLink,
  TelegramIcon,
  useMobileNavContext,
  Wallet,
  ConnectWalletBus,
} from '@silo/ui';
import {
  getWalletKeyFromSupportedWallet,
  SupportedWallet,
  useQueryNativeTokenBalance,
  useSiloWeb3,
  useWalletInfo,
  getWalletOptions,
} from '@silo/web3';
import { PATH } from 'app/hooks/useRoute';
import { Option, pipe } from 'effect';
import { useRouter } from 'next/router';
import { ReactNode, useEffect, useRef } from 'react';
import { connectWallet, mapTransactions } from './utils';
import { MobileRewardsTrigger } from './mobile-rewards-trigger';

export function MobileNav(): JSX.Element {
  const { protocolInstance, setProtocolInstance, account, activeWallet } = useSiloWeb3();
  const protocolKey = protocolInstance ?? DEFAULT_PROTOCOL_KEY;

  const nativeBalance = pipe(
    useQueryNativeTokenBalance(),
    Option.fromNullable,
    Option.map((bn) => bn.toBigInt()),
    Option.map(makeAmount),
    Option.getOrElse(() => AMOUNT_ZERO)
  );

  return (
    <BaseMobileNav
      logoLink={
        <UnstyledLink href={PATH.HOME}>
          <BaseMobileNav.LogoLinkContent />
        </UnstyledLink>
      }
      helpLink={
        <UnstyledLink href={LINKS.HELP} external className={BaseMobileNav.helpLink()}>
          <BaseMobileNav.HelpLinkContent />
        </UnstyledLink>
      }
      protocolKey={protocolKey}
      setProtocolKey={setProtocolInstance}
      runtimeProtocolKeys={RUNTIME_PROTOCOL_INSTANCES}
      primaryLinks={
        <BaseMobileNav.NavLinkContainer>
          <MobileNavLink path={PATH.HOME}>Markets</MobileNavLink>

          <MobileNavLink path={PATH.DASHBOARD}>Dashboard</MobileNavLink>

          <MobileNavLink path={LINKS.HELP} external>
            Help
          </MobileNavLink>
        </BaseMobileNav.NavLinkContainer>
      }
      secondaryLinks={
        <BaseMobileNav.NavLinkContainer>
          <MobileNavLink path={LINKS.DISCORD} external>
            <DiscordIcon />
            Discord
          </MobileNavLink>

          <MobileNavLink path={LINKS.TELEGRAM} external>
            <TelegramIcon />
            Telegram
          </MobileNavLink>
        </BaseMobileNav.NavLinkContainer>
      }
      walletTrigger={
        account && activeWallet ? (
          <ConnectedWalletTrigger
            protocolKey={protocolKey}
            account={account}
            wallet={activeWallet}
            nativeBalance={nativeBalance}
          />
        ) : (
          <DisconnectedWalletTrigger />
        )
      }
      walletFlow={
        account && activeWallet ? (
          <ConnectedWalletFlow
            protocolKey={protocolKey}
            account={account}
            wallet={activeWallet}
            nativeBalance={nativeBalance}
          />
        ) : (
          <DisconnectedWalletFlow />
        )
      }
      rewardsTrigger={<MobileRewardsTrigger protocolKey={protocolKey} />}
      contextSlot={<ContextSlot />}
    />
  );
}

interface MobileNavLinkProps {
  path: string;
  external?: boolean;
  children?: ReactNode;
}

function MobileNavLink({ path, children, external }: MobileNavLinkProps): JSX.Element {
  const { closeDialog } = useMobileNavContext();
  const router = useRouter();
  const isActive = path === router.route;

  return (
    <UnstyledLink onClick={closeDialog} className={BaseMobileNav.navLink({ isActive })} href={path} external={external}>
      {children}
    </UnstyledLink>
  );
}

function DisconnectedWalletTrigger(): JSX.Element {
  const { openWalletFlow } = useMobileNavContext();

  return <Wallet.DisconnectedTrigger onClick={openWalletFlow} className={BaseMobileNav.disconnectedTrigger()} />;
}

interface ConnectedWalletTriggerProps {
  protocolKey: ProtocolKey;
  account: ChecksumAddress;
  wallet: SupportedWallet;
  nativeBalance: Amount;
}

function ConnectedWalletTrigger({
  protocolKey,
  account: accountLegacy,
  wallet,
  nativeBalance,
}: ConnectedWalletTriggerProps): JSX.Element {
  const { openWalletFlow } = useMobileNavContext();
  const siloWeb3 = useSiloWeb3();

  const ensName = siloWeb3.ensName.data ?? undefined;
  const { isImpersonating } = siloWeb3;

  const chainKey = getChainKeyFromProtocol(protocolKey);
  const account = makeAddressSync(accountLegacy);
  const { walletInfoState } = useWalletInfo();
  const walletKey = getWalletKeyFromSupportedWallet(wallet);

  return (
    <Wallet.ConnectedTrigger
      nativeBalance={nativeBalance}
      chainKey={chainKey}
      account={account}
      state={walletInfoState}
      ensName={ensName}
      isImpersonating={isImpersonating}
      onClick={openWalletFlow}
      walletKey={walletKey}
      chevron={<BaseMobileNav.Chevron />}
    />
  );
}

function DisconnectedWalletFlow(): JSX.Element {
  const walletOptions = getWalletOptions();

  return <Wallet.DisconnectedFlow connectWallet={connectWallet} walletOptions={walletOptions} />;
}

interface ConnectedWalletFlowProps {
  protocolKey: ProtocolKey;
  account: ChecksumAddress;
  wallet: SupportedWallet;
  nativeBalance: Amount;
}

function ConnectedWalletFlow({
  protocolKey,
  account: accountLegacy,
  wallet,
  nativeBalance,
}: ConnectedWalletFlowProps): JSX.Element {
  const { closeDialog } = useMobileNavContext();
  const siloWeb3 = useSiloWeb3();

  const ensName = siloWeb3.ensName.data ?? undefined;
  const { isImpersonating, disconnect } = siloWeb3;

  const account = makeAddressSync(accountLegacy);
  const activeWalletKey = getWalletKeyFromSupportedWallet(wallet);
  const chainKey = getChainKeyFromProtocol(protocolKey);
  const walletInfo = useWalletInfo();
  const { clearTransactions } = walletInfo;
  const recentTransactions = mapTransactions(walletInfo.recentTransactions);
  const walletOptions = getWalletOptions();

  return (
    <Wallet.ConnectedFlow
      isImpersonating={isImpersonating}
      account={account}
      ensName={ensName}
      disconnect={disconnect}
      activeWalletKey={activeWalletKey}
      chainKey={chainKey}
      recentTransactions={recentTransactions}
      clearTransactions={clearTransactions}
      walletOptions={walletOptions}
      connectWallet={connectWallet}
      onConnect={closeDialog}
      nativeBalance={nativeBalance}
    />
  );
}

function ContextSlot(): null {
  const { openWalletFlow } = useMobileNavContext();

  const ref = useRef<() => void>();

  ref.current = openWalletFlow;

  useEffect(() => {
    const unsubscribe = ConnectWalletBus.subscribe(() => {
      ref.current?.();
    });

    return () => {
      unsubscribe();
    };
  }, []);

  return null;
}
